import { useEffect, useRef } from 'react';
import { useGameStore } from '../store/gameStore';
import { mapActivityToDweller } from './activityMapper';
import type { WsServerMessage, WsClientMessage } from '../types/ws';
import type { VentureType } from '../types';
import { terminalBridge } from './terminalBridge';
import { getBunkerSceneRef } from '../game/sceneBridge';
import { activityFromToolUse } from './errandActivity';
import { activityPhrase } from '../lib/thinkingVerbs';
import { getAccessToken } from './useAuth.tsx';
import { supabase } from '../lib/supabase.ts';

const RECONNECT_DELAYS_MS = [1000, 2000, 5000, 10000];

let globalSocket: WebSocket | null = null;

// Per-agent buffer to handle markdown delimiters split across stream chunks.
// We hold back only a trailing run of delimiter chars that could be the opening
// of a token split across chunks, then strip markdown on the joined buffer and
// flush the rest. Ordinary text is emitted immediately — no fixed trailing lag.
const chunkBuffers: Record<string, string> = {};

const MD_DELIM_CHARS = new Set(['*', '_', '`', '#', '-', '+']);

/**
 * How many trailing chars to withhold so a markdown token split across chunks
 * (e.g. "**", "__") can still be stripped once its second half arrives. Capped
 * at 2 (enough for the longest delimiter); 0 for ordinary text.
 */
export function markdownHoldLen(s: string): number {
  let hold = 0;
  while (hold < 2 && hold < s.length && MD_DELIM_CHARS.has(s[s.length - 1 - hold])) hold++;
  return hold;
}

// Full-response accumulator per agent: the store is updated once per response
// (on stream_end / stream_error), not once per chunk — per-chunk setState
// caused a React render cascade for every token while the terminal already
// renders live through terminalBridge.
const responseBuffers: Record<string, string> = {};

function stripMarkdown(input: string): string {
  return input
    // bold/italic delimiters
    .replace(/\*\*/g, '')
    .replace(/__/g, '')
    // inline code backticks (keep the content)
    .replace(/`+/g, '')
    // ATX headings at line start: "# ", "## ", "### "...
    .replace(/^[ \t]*#{1,6}[ \t]+/gm, '')
    // bullet markers at line start: "* ", "- ", "+ "
    .replace(/^[ \t]*[*\-+][ \t]+/gm, '  ')
    // single * for italic (avoid eating bare asterisks mid-word)
    .replace(/(^|[\s(])\*(\S[^*\n]*?)\*(?=[\s.,;:!?)]|$)/g, '$1$2');
}

function flushChunk(agentId: string, chunk: string): string {
  const combined = (chunkBuffers[agentId] ?? '') + chunk;
  const hold = markdownHoldLen(combined);
  const head = hold > 0 ? combined.slice(0, -hold) : combined;
  chunkBuffers[agentId] = hold > 0 ? combined.slice(-hold) : '';
  return stripMarkdown(head);
}

function flushTail(agentId: string): string {
  const tail = chunkBuffers[agentId] ?? '';
  delete chunkBuffers[agentId];
  return stripMarkdown(tail);
}

// Messages fired before the socket is OPEN (first renders race the connect
// handshake: list_bunkers, switch_bunker) are held here and flushed on open.
// Only genuine downtime — a CLOSED socket between reconnect attempts — warns.
const PENDING_MAX = 32;
let pendingMessages: WsClientMessage[] = [];

export function flushPendingWsMessages(): void {
  const queued = pendingMessages;
  pendingMessages = [];
  for (const m of queued) sendWsMessage(m);
}

export function sendWsMessage(msg: WsClientMessage): boolean {
  if (globalSocket && globalSocket.readyState === WebSocket.OPEN) {
    globalSocket.send(JSON.stringify(msg));
    return true;
  }
  const connecting = !globalSocket || globalSocket.readyState === WebSocket.CONNECTING;
  if (connecting && pendingMessages.length < PENDING_MAX) {
    pendingMessages.push(msg);
    return true;
  }
  // Surface the drop instead of failing silently — the founder sees it in the console log.
  useGameStore.getState().pushEvent('⚠ Server link down — message not sent');
  return false;
}

export function useActivityWebSocket(): void {
  const attemptRef = useRef(0);
  const closedRef = useRef(false);

  useEffect(() => {
    closedRef.current = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    const connect = () => {
      if (closedRef.current) return;
      // In dev mode connect directly to the backend to avoid Vite HMR proxy conflicts.
      // In production (Tauri / deployed) use same origin.
      const wsUrl = import.meta.env.DEV
        ? `ws://localhost:${import.meta.env.VITE_SERVER_PORT ?? '3001'}/ws`
        : `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws`;
      const token = getAccessToken();
      globalSocket = token
        ? new WebSocket(wsUrl, ['bearer', token])
        : new WebSocket(wsUrl);

      globalSocket.onopen = () => {
        attemptRef.current = 0;
        useGameStore.getState().setWsStatus('connected');
        // Always keep the bunker shelf populated regardless of which route the
        // founder lands on (deep-link to /bunker/:id needs the list for the
        // unknown-id redirect guard).
        sendWsMessage({ type: 'list_bunkers' });
        // Rebind the server socket to the bunker actually on screen. Every
        // connect binds server-side to the user's DEFAULT bunker, and a
        // reconnect (Supabase rotating the token when the window regains focus,
        // a dropped link) does not re-run BunkerPage's mount effect — so without
        // this the founder keeps working in one bunker while the server writes
        // into another, and the connect-time session_state push overwrites the
        // open board with the default bunker's progress.
        // On a first connect BunkerPage's mount effect has usually queued the
        // same message already; sending it twice would just cost a redundant
        // disk read server-side, so let the queue win when it has one.
        const openBunkerId = useGameStore.getState().activeBunkerId;
        const queuedSwitch = pendingMessages.some((m) => m.type === 'switch_bunker');
        if (openBunkerId && !queuedSwitch) sendWsMessage({ type: 'switch_bunker', id: openBunkerId });
        // Deliver anything queued while the handshake was in flight
        // (switch_bunker from BunkerPage's mount effect, etc.).
        flushPendingWsMessages();
      };

      globalSocket.onmessage = (e) => {
        let msg: WsServerMessage;
        try {
          msg = JSON.parse(e.data as string) as WsServerMessage;
        } catch {
          return;
        }
        
        const store = useGameStore.getState();

        if (msg.type === 'thinking_chunk') {
          // Accumulated in the terminal instance for the spinner peek; never persisted.
          terminalBridge.writeThinking(msg.agentId, msg.chunk);
          return;
        }

        if (msg.type === 'stream_chunk') {
          // Stop the thinking spinner the moment text starts flowing.
          store.stopThinking(msg.agentId);
          responseBuffers[msg.agentId] = (responseBuffers[msg.agentId] ?? '') + msg.chunk;
          const visible = flushChunk(msg.agentId, msg.chunk).replace(/\r?\n/g, '\r\n');
          if (visible) terminalBridge.write(msg.agentId, visible);
          return;
        }

        if (msg.type === 'tool_use') {
          // Commit any held-back answer text, then surface the tool as a single
          // dynamic status phrase on the live working line — no permanent
          // transcript line. The status line stays live through the whole
          // pre-answer phase, so the phrase shows without re-arming the spinner.
          const pending = flushTail(msg.agentId).replace(/\r?\n/g, '\r\n');
          if (pending) terminalBridge.write(msg.agentId, pending);
          terminalBridge.setActivity(msg.agentId, activityPhrase(msg.name, msg.input));
          // Stationary sprite tell: reflect the coarse tool activity in the
          // existing thinking bubble. No movement — safe at idle.
          getBunkerSceneRef()?.setActivityTell(msg.agentId, activityFromToolUse(msg.name));
          return;
        }

        if (msg.type === 'stream_error') {
          store.stopThinking(msg.agentId);
          // Flush the held-back markdown tail so it can't bleed into the next response.
          const pending = flushTail(msg.agentId).replace(/\r?\n/g, '\r\n');
          if (pending) terminalBridge.write(msg.agentId, pending);
          // Keep whatever partial answer arrived: it's real conversation history.
          const partial = responseBuffers[msg.agentId] ?? '';
          delete responseBuffers[msg.agentId];
          if (partial.length > 0) store.appendChatMessage(msg.agentId, 'assistant', partial);
          terminalBridge.write(msg.agentId, `\x1b[31m\r\n[error] ${msg.message}\x1b[0m`);
          // No endStream here: the server always follows stream_error with stream_end.
          return;
        }

        if (msg.type === 'quota_exceeded') {
          // No agentId — stop every active thinking agent, flush their buffers,
          // and end their streams so the founder's input is immediately usable.
          // Mirror stream_error per agent, but drive it from the active thinking set.
          // If no agent is mid-thinking yet (quota_exceeded raced ahead of startThinking),
          // the loop no-ops; pushEvent below is the user-visible safety net.
          const activeAgents = Object.keys(store.thinkingAgents);
          for (const aid of activeAgents) {
            store.stopThinking(aid);
            const pending = flushTail(aid).replace(/\r?\n/g, '\r\n');
            if (pending) terminalBridge.write(aid, pending);
            const partial = responseBuffers[aid] ?? '';
            delete responseBuffers[aid];
            if (partial.length > 0) store.appendChatMessage(aid, 'assistant', partial);
            terminalBridge.write(aid, `\x1b[31m\r\n[quota] ${msg.message}\x1b[0m`);
            terminalBridge.endStream(aid);
          }
          // Surface the notice in the event feed (visible on all panels).
          store.pushEvent(`⚠ ${msg.message}`);
          return;
        }

        if (msg.type === 'upgrade_required') {
          store.showUpgradePrompt({ era: msg.era, reason: msg.reason });
          return;
        }

        if (msg.type === 'stream_end') {
          store.stopThinking(msg.agentId);
          const tail = flushTail(msg.agentId).replace(/\r?\n/g, '\r\n');
          if (tail) terminalBridge.write(msg.agentId, tail);
          const full = responseBuffers[msg.agentId] ?? '';
          delete responseBuffers[msg.agentId];
          if (full.length > 0) store.appendChatMessage(msg.agentId, 'assistant', full);
          terminalBridge.endStream(msg.agentId);
          store.bumpArtifacts(msg.agentId);
          // A turn may have written a deliverable the watcher missed (new user
          // dir, debounce race). Ask for the truth; the reply is idempotent.
          sendWsMessage({ type: 'refresh_quests' });
          return;
        }

        if (msg.type === 'session_state') {
          // Whose state is this? The connect-time push is always about the
          // user's DEFAULT bunker, so on a reconnect it describes the wrong one.
          // Applying it merged the first bunker's completed quests (and its era,
          // phase and venture type) into whichever bunker was open — quests
          // unlocking and XP jumping on window focus. The switch_bunker sent on
          // open corrects the server; this drops the stale push that raced it.
          // Plan is user-scoped, so it is safe to take from any snapshot.
          store.setPlan(msg.plan);
          const openBunkerId = store.activeBunkerId;
          if (openBunkerId && msg.bunkerId !== openBunkerId) return;
          if (msg.state) {
            const era = Math.min(Math.max(msg.state.era, 0), 5) as 0 | 1 | 2 | 3 | 4 | 5;
            store.setEra(era);
            store.setMgtPhase(msg.state.mgtPhase);
            store.setVentureType(msg.state.ventureType as VentureType | null);
            store.setOnboardingComplete(msg.state.onboardingComplete);
          } else {
            // No server state (a brand-new bunker, fresh install, or post-reset):
            // force re-onboarding AND rewind progression to Era 0, even if stale
            // localStorage from another venture claims otherwise (zombie state).
            store.setEra(0);
            store.setMgtPhase(0);
            store.setOnboardingComplete(false);
            store.setVentureType(null);
          }
          // Quest done-state is per-bunker. When entering a venture different from
          // the one the persisted board belongs to, wipe it (clearing manual
          // overrides) and rebuild purely from the server snapshot — otherwise a
          // fresh bunker would inherit the previous venture's completed quests.
          // Within the same bunker we merge so manual checkmarks survive reloads.
          const activeId = store.activeBunkerId;
          if (activeId && activeId !== store.questsBunkerId) {
            store.resetQuestsToServer(msg.questCompletions);
            store.setQuestsBunkerId(activeId);
          } else {
            for (const [questId, auto] of Object.entries(msg.questCompletions)) {
              store.setQuestAutoCompleted(questId, auto);
            }
          }
          // Signal hydration AFTER applying state: the onboarding trigger
          // gates on this so it never fires from stale local data.
          store.setSessionHydrated(true);
          return;
        }

        if (msg.type === 'inbox_updated') {
          store.bumpInbox(msg.agentId);
          return;
        }

        if (msg.type === 'agent_errand') {
          if (msg.phase === 'started') {
            getBunkerSceneRef()?.enqueueErrand({
              id: msg.errandId,
              actor: msg.actor,
              target: msg.target,
              kind: msg.kind,
            });
          } else if (msg.phase === 'finished') {
            // Long deep consults: server signals completion explicitly so the
            // sync line doesn't linger past the in-bunker errand animation.
            getBunkerSceneRef()?.onErrandFinished();
          }
          return;
        }

        if (msg.type === 'conversation_saved') {
          // Remember the file the server just wrote so the next message in this
          // thread updates it in place rather than creating a new conversation.
          store.setActiveConversationFile(msg.agentId, msg.filename);
          store.bumpHistory(msg.agentId);
          return;
        }

        if (msg.type === 'phase_verdict') {
          if (msg.verdict === 'greenlight') {
            const newEra = Math.min(msg.phase + 1, 5) as 0 | 1 | 2 | 3 | 4 | 5;
            store.setEra(newEra);
            store.setPendingUnlockAnimation(newEra);
            store.pushEvent(`✓ Era ${msg.phase} validated — Era ${newEra} unlocked`);
          } else if (msg.verdict === 'pivot' || msg.verdict === 'kill') {
            const target = Math.min(Math.max(msg.targetEra ?? 1, 0), 5) as 0 | 1 | 2 | 3 | 4 | 5;
            store.rewindToEra(target);
            store.setPendingUnlockAnimation(target);
            const glyph = msg.verdict === 'pivot' ? '⟲ PIVOT' : '☠ KILL';
            store.pushEvent(`${glyph} — rewound to Era ${target}: ${msg.reason ?? 'strategic decision'}`);
          } else {
            store.pushEvent(`✗ Era ${msg.phase} blocked: ${msg.reason ?? 'no reason given'}`);
          }
          return;
        }

        if (msg.type === 'quest_completion_changed') {
          // The watcher reports on every bunker this founder owns, so an event
          // for a bunker that isn't on screen must not tick its board.
          const openBunkerId = store.activeBunkerId;
          if (openBunkerId && msg.bunkerId !== openBunkerId) return;
          store.setQuestAutoCompleted(msg.questId, msg.autoCompleted);
          return;
        }

        if (msg.type === 'quest_completions') {
          // Same bunker-stamp rule as `session_state` and
          // `quest_completion_changed`: a snapshot for a bunker that is not on
          // screen must never tick the open board.
          const openBunkerId = store.activeBunkerId;
          if (openBunkerId && msg.bunkerId !== openBunkerId) return;
          for (const [questId, auto] of Object.entries(msg.questCompletions)) {
            store.setQuestAutoCompleted(questId, auto);
          }
          return;
        }

        if (msg.type === 'onboarding_complete') {
          // Applied straight to the store — App derives its toast from the transition.
          store.setOnboardingComplete(true);
          store.setVentureType(msg.ventureType);
          store.pushEvent('✓ Phase 0 complete — bunker unlocked');
          return;
        }

        if (msg.type === 'reset_complete') {
          // Window event (UI feedback in SettingsPanel) — convention: always window, never document.
          window.dispatchEvent(new CustomEvent('foundrx:reset_complete'));
          return;
        }

        if (msg.type === 'bunkers_list') {
          store.setBunkers(msg.bunkers);
          return;
        }

        if (msg.type === 'founder_stats') {
          store.setFounderStats(msg.stats);
          return;
        }

        if (msg.type === 'bunker_created') {
          // HomePage watches justCreatedBunkerId to navigate into the new bunker.
          store.setJustCreatedBunkerId(msg.id);
          // Refresh the shelf so the new bunker is present before navigation.
          sendWsMessage({ type: 'list_bunkers' });
          return;
        }

        if (msg.type === 'bunker_error') {
          store.pushEvent(`⚠ ${msg.message}`);
          return;
        }

        if (msg.type !== 'activity') return;

        const intent = mapActivityToDweller(msg.entry);
        if (!intent) return;

        if (store.dwellerMovements[intent.dwellerId]?.state === 'walking') return;
        store.dispatchDweller(intent.dwellerId, intent.targetRoom);
      };

      globalSocket.onclose = () => {
        if (closedRef.current) return;
        globalSocket = null;
        useGameStore.getState().setWsStatus('reconnecting');
        const delay = RECONNECT_DELAYS_MS[Math.min(attemptRef.current, RECONNECT_DELAYS_MS.length - 1)];
        attemptRef.current += 1;
        reconnectTimer = setTimeout(connect, delay);
      };

      globalSocket.onerror = () => globalSocket?.close();
    };

    connect();

    // Reconnect with the fresh token whenever Supabase rotates it.
    const { data: authSub } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'TOKEN_REFRESHED' || event === 'SIGNED_IN' || event === 'SIGNED_OUT') {
        globalSocket?.close(); // onclose → existing reconnect path picks up the new token
      }
    });

    return () => {
      closedRef.current = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      globalSocket?.close();
      globalSocket = null;
      authSub.subscription.unsubscribe();
    };
  }, []);
}
